//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import React, { useState } from 'react';
import styled, { css } from 'styled-components';

import { ItemProductSearchResult } from '@apis/universal-image-actions/universalImageActionsApiModel';
import { OverlayIconButton } from '@components/atoms/button/Button';
import { ImageWithErrorHandling } from '@components/atoms/image-with-error-handling/ImageWithErrorHandling';
import { UiaProductTrackingIds } from '@uia/products/constants/UiaProductConstants';
import { withTracking } from '@utils/withTracking';

export interface UiaProductMediaProps {
  isSelected: boolean;
  product: ItemProductSearchResult;
  onSelect: () => void;
}

export const UiaProductMedia = ({
  isSelected,
  onSelect,
  product,
}: UiaProductMediaProps) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <Wrapper
      $isSelected={isSelected}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <ThumbnailButton
        onClick={onSelect}
        trackingId={UiaProductTrackingIds.BULK_SELECT_PRODUCT}
        type='button'
      >
        <Thumbnail
          alt={product.name ?? 'product image'}
          src={product.images[0]?.sourceImageUrl}
        />
      </ThumbnailButton>
      {(isSelected || isHovered) && (
        <StyledOverlayIconButton
          $isSelected={isSelected}
          iconName='checkmark'
          onClick={onSelect}
          trackingId={UiaProductTrackingIds.BULK_SELECT_PRODUCT}
        />
      )}
    </Wrapper>
  );
};

const Wrapper = styled.div<{ $isSelected: boolean }>(
  ({ $isSelected, theme }) => css`
    position: relative;
    border-radius: ${theme.borderWidths[3]};
    aspect-ratio: 1;
    overflow: hidden;
    ${$isSelected &&
    `border: ${theme.borderWidths[3]} solid ${theme.colors.aliases.primary};`}
  `,
);

const ThumbnailButton = withTracking(styled.button`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  width: 100%;
  border: unset;
  padding: unset;
  box-shadow: unset;
  overflow: hidden;

  :hover {
    box-shadow: unset;
  }
`);

const Thumbnail = styled(ImageWithErrorHandling)`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const StyledOverlayIconButton = styled(OverlayIconButton)<{
  $isSelected: boolean;
}>(
  ({ $isSelected, theme }) => css`
    position: absolute;
    top: ${theme.spacings[2]};
    left: ${theme.spacings[2]};
    ${$isSelected && `background-color: ${theme.colors.aliases.primary};`}
  `,
);
